import { Component, ViewEncapsulation, OnDestroy } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription, forkJoin, of, catchError, switchMap } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgClass, NgFor } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { DocumentService } from '../../services/documents.service';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { Document, DocumentLine, FieldOfDocument, OrderNumbers } from '../../models/document';
import { batchesFilter } from '../../utils/batches.utils';
import { UserService } from '../../../shared/services/user.service';
import { BatchesService } from '../../../order/services/batches.service';
import { Batch } from '../../../order/models/batch';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { DbAnalyticsService } from '../../../shared/services/db-analytics.service';

@Component({
	selector: 'copo-dispatch-advice-card',
	templateUrl: './dispatch-advice-card.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		BackButtonComponent,
		TranslateModule,
		MatRippleModule,

		MatButtonModule,
		MatIconModule,
		AfterViewInitDirective,
		MatExpansionModule,
		MatDividerModule,
		NgClass,
		NgFor,
		RouterLink,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
		SafeUrlPipe
	]
})
export class DispatchAdviceCardComponent implements OnDestroy {
	dispatchAdviceFields: readonly FieldOfDocument[] = [
		'id',
		'name',
		'payerName',
		'transportMode',
		'dispatchAdviceDate',
		'soldtoName',
		'shiptoName',
		'millCode',
		'documentLines'
	];

	dispatchAdviceData?: Document;
	batchData: PaginatedResult<Batch> | undefined;
	dispatchAdviceDisplayed = false;
	id: number;
	dispatchAdviceSub: Subscription;
	isLoading = false;
	isOffline = false;
	isDocumentNotFound = false;
	allBatchesDisplayed = true;
	orderNumbers: OrderNumbers[] = [];
	readonly weightDisplay: string;
	readonly UNDERSCORE = UNDERSCORE;

	constructor(
		private readonly documentService: DocumentService,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly batchService: BatchesService,
		private readonly dbAnalyticsService: DbAnalyticsService
	) {
		this.id = <number> this.route.snapshot.params['id'];
		this.isOffline = !navigator.onLine;
		const { user } = this.userService;
		this.dispatchAdviceDisplayed = user.hasServiceParameterEqualsTo('PDOCS', 'DispatchAdvice', 'True');
		const weightSettings = user.getSettings('weight');
		this.weightDisplay = weightSettings ? weightSettings.value.weight : 'net';
		this.isLoading = true;
		this.dispatchAdviceSub = this.documentService.getDocumentDetails(this.id, this.dispatchAdviceFields)
			.pipe(
				switchMap(
					dispatchAdviceData => forkJoin([
						of(dispatchAdviceData),
						this.getDispatchAdviceBatches(dispatchAdviceData.documentLines),
					])
				)
			)
			.subscribe({
				next: ([dispatchAdviceData, batchData]) => {
					if (Object.keys(dispatchAdviceData).length === 0) {
						this.isDocumentNotFound = true;
						this.isLoading = false;
					}
					this.dispatchAdviceData = dispatchAdviceData;
					this.batchData = batchData;
					for (const dispatchAdvice of this.dispatchAdviceData.documentLines) {
						dispatchAdvice.isSelected = true;
					}
					this.isLoading = false;
					this.getUniqueOrderName();
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
				}
			});
	}

	getDispatchAdviceBatches(documentLine: DocumentLine[] | undefined) {
		if (documentLine === undefined) {
			return of(undefined);
		}
		return this.batchService.getBatches(
			[
				'qtyBatchNet',
				'qtyBatchGross',
				'batchReference',
				'length'
			],
			batchesFilter(documentLine.map(dispatchAdviceBatch => dispatchAdviceBatch.batchNumber)),
			0,
			50,
			new OrderBy('batchReference', OrderByDirection.DESC),
			false
		)
			.pipe(catchError((err: unknown) => {
				console.error('Batch not found :', err);
				return of(undefined);
			}));
	}

	getQtyBatch(batchReference: string, type: string) {
		if (this.batchData) {
			switch (type) {
			case 'WEIGHT':
				for (const batch of this.batchData.results) {
					if (batch.batchReference === batchReference) {
						if (this.weightDisplay === 'net') {
							return batch.qtyBatchNet;
						}
						return batch.qtyBatchGross;
					}
				}
				break;
			case 'LENGTH':
				for (const batch of this.batchData.results) {
					if (batch.batchReference === batchReference) {
						return batch.length;
					}
				}
				break;
			}
		}
		return UNDERSCORE;
	}

	getUniqueOrderName() {
		const orderReference = new Set<string>();
		if (this.dispatchAdviceData?.documentLines === undefined) {
			return;
		}
		for (const docLine of this.dispatchAdviceData.documentLines) {
			if (docLine.customerOrderItemReference) {
				orderReference.add(`${ docLine.customerOrderReference }/${ docLine.customerOrderItemReference }`);
			} else {
				orderReference.add(docLine.customerOrderReference);
			}
		}
		for (const orderRef of orderReference) {
			const orderNumber = {
				key: orderRef,
				isSelected: false
			};
			this.orderNumbers.push(orderNumber);
		}
	}

	getOrderName(docLine: DocumentLine, type: string) {
		if (type === 'order') {
			if (docLine.customerOrderReference && docLine.customerOrderItemReference) {
				return `${ docLine.customerOrderReference }/${ docLine.customerOrderItemReference }`;
			} else if (docLine.customerOrderReference && !docLine.customerOrderItemReference) {
				return docLine.customerOrderReference;
			}
		}
		if (type === 'millOrder') {
			if (docLine.millOrderReference && docLine.millOrderReferenceItem) {
				return `${ docLine.millOrderReference }/${ docLine.millOrderReferenceItem }`;
			} else if (docLine.millOrderReference && !docLine.millOrderReferenceItem) {
				return docLine.customerOrderReference;
			}
		}
		return UNDERSCORE;
	}

	displayAllBatches() {
		this.allBatchesDisplayed = true;
		if (this.dispatchAdviceData?.documentLines === undefined) {
			return;
		}
		for (const docLine of this.dispatchAdviceData.documentLines) {
			docLine.isSelected = true;
		}
	}

	filterBatch(orderNumber: OrderNumbers) {
		this.allBatchesDisplayed = false;
		if (this.dispatchAdviceData?.documentLines === undefined) {
			return;
		}
		for (const orderNum of this.orderNumbers) {
			orderNum.isSelected = orderNumber.key === orderNum.key;
		}
		for (const line of this.dispatchAdviceData.documentLines) {
			line.isSelected = orderNumber.key === (
				line.customerOrderItemReference
					? `${ line.customerOrderReference }/${ line.customerOrderItemReference }`
					: line.customerOrderReference
			);
		}
	}

	getDownloadDocumentUrl(id: number, filename: string) {
		return this.documentService.getDownloadDocumentUrl(id, filename);
	}

	trackDownload(id: number) {
		this.dbAnalyticsService.trackAction('DOWNLOAD_PDF', {
			documentType: 'DISPATCH_ADVICE',
			documentId: String(id)
		});
	}

	getDocumentURL(id: number, filename: string) {
		return this.documentService.getViewDocumentUrl(id, filename);
	}

	ngOnDestroy() {
		this.dispatchAdviceSub.unsubscribe();
	}
}
